import React from 'react';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import { useTeamsProfile } from '../../hooks/use-hydra';
import styled, { useTheme } from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { H3, P, PExtraSmall, PSmall } from '../../components/elements/Text';
import { MultiLineInput } from '../../components/elements/Input';
import { ColumnQuestionWrapper } from '../../components/Onboarding/Typeform/common';
import autoAnimate from '@formkit/auto-animate';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import useAuth from '../../hooks/use-auth';
import { saveTeamsProfile } from '../../api/teams';
import {
  NavigationFooter,
  AnimationContainer,
  ScrollContainer,
  NavButton,
  MultiSelectQuestion,
} from './TeamsCohortOnboarding';
import { ROUTES } from '../../routing';

type MultiSelectAnswer = string[];

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const ProgressWidget = ({
  currentIndex,
  totalQuestions,
  label,
}: {
  currentIndex: number;
  totalQuestions: number;
  label: string;
}) => {
  const answeredCount = currentIndex + 1;

  return (
    <ProgressContainer>
      <ProgressText style={{ fontSize: 12 }}>
        {label.toUpperCase()}
      </ProgressText>

      <ProgressBarContainer>
        <ProgressBar progress={(answeredCount / totalQuestions) * 100} />
      </ProgressBarContainer>
      <ProgressText>
        {answeredCount} of {totalQuestions} answered
      </ProgressText>
    </ProgressContainer>
  );
};

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ProgressText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 500;
  margin: 0;
  padding: 0;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${({ theme }) => theme.colors.divider};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${({ progress }) => progress}%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
  transition: width 0.3s ease-in-out;
`;

export default function TeamsProfileOnboarding() {
  const {
    value: teamsProfile,
    retry: refreshTeamsData,
    loading: isLoadingTeams,
    error,
  } = useTeamsProfile();
  const { getTokenSilently } = useAuth();
  const history = useHistory();
  const { companySlug } = useParams<{ companySlug: string | undefined }>();
  const saveProfileMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const parsedAnswers = {
        'ask-name': answers?.['ask-name'] as string,
        'ask-role': answers?.['ask-role'] as string,
        'ask-ideal-teacher': answers?.['ask-ideal-teacher'] as string[],
      };
      const response = await saveTeamsProfile(
        token,
        parsedAnswers,
        teamsProfile?.learnerId ?? -1
      );
      return response;
    },
    onError: (error: Error | AxiosError) => {
      toast.error(`Failed to save profile - ${error.message}`);
    },
    onSuccess: async () => {
      if ((teamsProfile?.openCohortInvites ?? []).length > 0) {
        history.push(
          ROUTES.TEAMS.COHORT_ONBOARDING.getPath({
            teamSlug: companySlug ?? '',
          })
        );
      } else {
        await refreshTeamsData();
        history.push(ROUTES.HOME.getPath());
      }
    },
  });

  const QUESTIONS = React.useMemo(
    () => [
      {
        id: 'ask-name',
        text: "Welcome! What's your full name?",
        type: 'input',
      },
      {
        id: 'ask-role',
        text: `What is your role at ${teamsProfile?.company?.name}?`,
        subtext: 'This will help us customize your program.',
        type: 'input',
      },
      {
        id: 'ask-ideal-teacher',
        text: 'Think back to your favorite teacher. What was great about them?',
        type: 'multi-select',
        options: [
          {
            value: 'Explained things in a way that was clear and simple',
            label: 'Explained things in a way that was clear and simple',
          },
          {
            value: 'Spent time helping me individually',
            label: 'Spent time helping me individually',
          },
          {
            value: 'They were very likeable / funny',
            label: 'They were very likeable / funny',
          },

          {
            value: 'Their lessons or exercises were engaging',
            label: 'Their lessons or exercises were engaging',
          },
          {
            value: 'Was very good/strict about ensuring I got work done',
            label: 'Was very good/strict about ensuring I got work done',
          },

          {
            value: 'They were incredibly knowledgeable',
            label: 'They were incredibly knowledgeable',
          },

          { value: 'Other', label: 'Other' },
        ],
      },
    ],
    [teamsProfile?.company?.name]
  );

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<
    Record<string, string | MultiSelectAnswer>
  >(() => {
    // Initialize with existing profile data if available
    const existingAnswers: Record<string, string | MultiSelectAnswer> = {};
    const profileData = teamsProfile?.onboardingAnswers;
    if (profileData) {
      QUESTIONS.forEach((question) => {
        const answer = profileData?.[question.id as keyof typeof profileData];
        if (answer) {
          existingAnswers[question.id] = answer;
        }
      });
    }
    return existingAnswers;
  });

  const theme = useTheme();
  const animationParentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (animationParentRef.current) {
      autoAnimate(animationParentRef.current, {
        duration: 300,
        easing: 'ease-in-out',
      });
    }
  }, [animationParentRef]);

  const currentQuestion = QUESTIONS[currentIndex];
  const currentQuestionAnswer = answers[currentQuestion.id] || '';
  const isCurrentQuestionAnswered = React.useMemo(() => {
    const answer = answers[currentQuestion.id];
    if (currentQuestion.type === 'multi-select') {
      return Array.isArray(answer) && answer.length > 0;
    }
    return typeof answer === 'string' && answer.trim().length > 0;
  }, [answers, currentQuestion]);

  React.useEffect(() => {
    if (teamsProfile?.onboardingAnswers && !saveProfileMutation.isLoading) {
      history.push(ROUTES.HOME.getPath());
    }
  }, [teamsProfile, history, saveProfileMutation.isLoading]);

  const handleAnswerQuestion = (
    id: string,
    answer: string | MultiSelectAnswer
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [id]: answer,
    }));
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (!isCurrentQuestionAnswered) {
      return;
    }
    if (
      currentIndex === QUESTIONS.length - 1 &&
      !saveProfileMutation.isLoading
    ) {
      saveProfileMutation.mutate();
    } else if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <PageWithAsyncData
      isLoading={isLoadingTeams}
      error={error}
      retry={refreshTeamsData}
    >
      <ScrollContainer ref={animationParentRef}>
        <AnimationContainer key={`animation-${currentQuestion.id}`}>
          <Column key={`column-${currentQuestion.id}`}>
            {currentQuestion.type === 'input' && (
              <InputQuestion
                id={currentQuestion.id}
                key={currentQuestion.id}
                text={currentQuestion.text}
                subtext={currentQuestion.subtext}
                answer={(currentQuestionAnswer as string) || ''}
                onAnswerQuestion={handleAnswerQuestion}
                onNext={handleNext}
              />
            )}
            {currentQuestion.type === 'multi-select' && (
              <MultiSelectQuestion
                id={currentQuestion.id}
                text={currentQuestion.text}
                subtext={currentQuestion.subtext}
                answer={(currentQuestionAnswer as MultiSelectAnswer) || []}
                options={currentQuestion.options || []}
                onAnswerQuestion={handleAnswerQuestion}
              />
            )}
            <NavButton
              label={currentIndex === QUESTIONS.length - 1 ? 'Submit' : 'Next'}
              onClick={handleNext}
              isDisabled={
                saveProfileMutation.isLoading || !isCurrentQuestionAnswered
              }
              style={{
                marginTop: '32px',
              }}
              type="primary"
              faIcon={
                currentIndex === QUESTIONS.length - 1
                  ? 'fas fa-check'
                  : 'fas fa-arrow-right'
              }
            />
          </Column>
        </AnimationContainer>
        <NavigationFooter>
          <NavButton
            label="Previous"
            onClick={handlePrevious}
            faIcon="fas fa-arrow-left"
            isDisabled={currentIndex === 0}
            showIconBeforeLabel
            color={theme.colors.textRegular}
            style={{
              border: 'none',
              marginTop: 0,
            }}
          />
          <ProgressWidget
            label="PROFILE"
            currentIndex={currentIndex}
            totalQuestions={QUESTIONS.length}
          />
        </NavigationFooter>
      </ScrollContainer>
    </PageWithAsyncData>
  );
}

const Label = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  font-weight: 400;
  margin-top: 6px;
`;

function InputQuestion({
  id,
  text,
  subtext,
  answer,
  onAnswerQuestion,
  onNext,
}: {
  id: string;
  text: string;
  subtext?: string;
  answer: string;
  onAnswerQuestion: (id: string, answer: string) => void;
  onNext: () => void;
}) {
  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      {subtext && <P>{subtext}</P>}
      <MultiLineInput
        autoFocus
        value={answer}
        placeholder="Enter your answer here"
        onChange={(e) => onAnswerQuestion(id, e.target.value)}
        shouldSubmitOnEnter
        onSubmit={onNext}
        canSubmit={answer.trim().length > 0}
      />
      <Label>Shift+Enter to make a line break</Label>
    </ColumnQuestionWrapper>
  );
}
